import React, { FC, useMemo, useState } from 'react';
import ActionList from '@components/ActionsMenu/ActionList';
import { IActionItem } from '@components/ActionsMenu/types';
import useStore from '@components/MainStage/store';
import { EntityValidationStatus, Modes } from '@constants/canvas/layers';
import { useProject } from '@context/Project/ProjectProvider';
import { useToast } from '@context/Toast/ToastProvider';
import useAssets from '@hooks/useAssets';
import useSaveEntitiesResponse from '@hooks/useSaveEntitiesResponse';
import { useSetNodesValidationStatusMutation } from '@store/services/nodes';
import { NodeDTO } from '@store/services/nodes/types';
import {
  getValidationChangePlan,
  ValidationChangePlan,
} from '@utils/validationStatusEligibility';

import { SetValidationStatusModal } from './SetValidationStatusModal';

const SUCCESS_MESSAGE = 'Validation status has been successfully updated.';
const DISABLED_STATUS_TOOLTIP =
  'This status cannot be applied to the selected entities.';

type Props = { nodeIds: string[]; onCloseMenu: () => void };

export const ValidationStatusMenu: FC<Props> = ({ nodeIds, onCloseMenu }) => {
  const { getNodeById } = useStore();
  const {
    toolbox: { mode },
  } = useProject();
  const resolvedMode = mode ?? Modes.BusinessAlignment;
  const { selectedAssetId } = useAssets();
  const { showToast } = useToast();
  const { saveResponseToRedux } = useSaveEntitiesResponse();
  const [setNodesValidationStatus] = useSetNodesValidationStatusMutation();

  const [pendingStatus, setPendingStatus] =
    useState<EntityValidationStatus | null>(null);

  const nodes = useMemo(
    () =>
      nodeIds
        .map((id) => getNodeById(id)?.data?.dto as NodeDTO | undefined)
        .filter((dto): dto is NodeDTO => Boolean(dto)),
    [nodeIds, getNodeById],
  );

  const draftPlan = useMemo(
    () =>
      getValidationChangePlan(
        nodes,
        resolvedMode,
        EntityValidationStatus.Draft,
      ),
    [nodes, resolvedMode],
  );
  const validatedPlan = useMemo(
    () =>
      getValidationChangePlan(
        nodes,
        resolvedMode,
        EntityValidationStatus.Validated,
      ),
    [nodes, resolvedMode],
  );

  const planByStatus: Record<EntityValidationStatus, ValidationChangePlan> = {
    [EntityValidationStatus.Draft]: draftPlan,
    [EntityValidationStatus.Validated]: validatedPlan,
  };
  const pendingPlan = pendingStatus ? planByStatus[pendingStatus] : null;

  const apply = async (valid_status: EntityValidationStatus) => {
    const response = await setNodesValidationStatus({
      nodeIds,
      valid_status,
      mode: resolvedMode,
      assetId: selectedAssetId || undefined,
    }).unwrap();

    saveResponseToRedux(response);
    showToast(SUCCESS_MESSAGE, 'success');
  };

  const isDraftDisabled = draftPlan.toChange.length === 0;
  const isValidatedDisabled = validatedPlan.toChange.length === 0;

  const items: IActionItem[] = [
    {
      text: 'Draft',
      disabled: isDraftDisabled,
      tooltip: isDraftDisabled ? DISABLED_STATUS_TOOLTIP : undefined,
      onClick: () => setPendingStatus(EntityValidationStatus.Draft),
    },
    {
      text: 'Validated',
      disabled: isValidatedDisabled,
      tooltip: isValidatedDisabled ? DISABLED_STATUS_TOOLTIP : undefined,
      onClick: () => setPendingStatus(EntityValidationStatus.Validated),
    },
  ];

  return (
    <>
      <ActionList items={items} />

      <SetValidationStatusModal
        open={pendingStatus !== null}
        toChange={pendingPlan?.toChange ?? []}
        unchanged={pendingPlan?.unchanged ?? []}
        onBack={() => setPendingStatus(null)}
        onConfirm={async () => {
          if (pendingStatus) {
            await apply(pendingStatus);
          }
          setPendingStatus(null);
          onCloseMenu();
        }}
      />
    </>
  );
};
